import { Component } from '@theme/component';

/**
 * Announcement banner custom element that slides content horizontally.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;
  #isAnimating = false;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.play();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    const slides = this.refs.slides ?? [];
    if (slides.length === 0 || this.#isAnimating) return;

    const previousIndex = ((this.#current % slides.length) + slides.length) % slides.length;
    const nextIndex = ((current % slides.length) + slides.length) % slides.length;
    if (previousIndex === nextIndex) return;

    const outgoing = slides[previousIndex];
    const incoming = slides[nextIndex];
    const direction = current > this.#current ? 1 : -1;
    this.#current = current;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      outgoing.setAttribute('aria-hidden', 'true');
      incoming.setAttribute('aria-hidden', 'false');
      return;
    }

    this.#isAnimating = true;
    outgoing.classList.add('announcement-bar__slide--leaving');
    outgoing.setAttribute('aria-hidden', 'true');
    incoming.setAttribute('aria-hidden', 'false');

    const options = { duration: 700, easing: 'cubic-bezier(0.45, 0, 0.2, 1)', fill: 'both' };
    const leaving = outgoing.animate(
      [{ transform: 'translateX(0)' }, { transform: 'translateX(' + (-100 * direction) + '%)' }],
      options
    );
    const entering = incoming.animate(
      [{ transform: 'translateX(' + (100 * direction) + '%)' }, { transform: 'translateX(0)' }],
      options
    );

    void Promise.allSettled([leaving.finished, entering.finished]).then(() => {
      leaving.cancel();
      entering.cancel();
      outgoing.classList.remove('announcement-bar__slide--leaving');
      this.#isAnimating = false;
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
